<template>
    <v-container fluid class="painel-shell">

        <template v-if="$store.state.userType === 'professor'">

            <!-- Saudação -->
            <v-row class="mb-1">
                <v-col>
                    <p class="text-h5 font-weight-bold painel-title">
                        Olá, {{ $store.state.userName }}
                    </p>
                    <p class="text-body-2 text-medium-emphasis">
                        Aqui está um resumo das suas atividades
                    </p>
                </v-col>
            </v-row>

            <!-- KPI cards -->
            <v-row>
                <v-col cols="12" sm="4">
                    <v-card class="painel-kpi-card" elevation="0">
                        <v-card-text class="d-flex align-center ga-4 pa-5">
                            <div class="painel-kpi-icon" style="background: rgba(60,110,143,0.12);">
                                <v-icon color="secondary" icon="mdi-help-box-multiple" size="30" />
                            </div>
                            <div>
                                <div class="painel-kpi-number">
                                    <v-progress-circular v-if="loadingNQ" color="secondary"
                                                         indeterminate size="30" width="3" />
                                    <span v-else>{{ nQuestions }}</span>
                                </div>
                                <p class="text-body-2 text-medium-emphasis mt-1">Meus Questionários</p>
                            </div>
                        </v-card-text>
                        <div class="painel-kpi-accent" style="background: #3C6E8F;" />
                    </v-card>
                </v-col>

                <v-col cols="12" sm="4">
                    <v-card class="painel-kpi-card" elevation="0">
                        <v-card-text class="d-flex align-center ga-4 pa-5">
                            <div class="painel-kpi-icon" style="background: rgba(22,50,74,0.10);">
                                <v-icon color="primary" icon="mdi-note-multiple" size="30" />
                            </div>
                            <div>
                                <div class="painel-kpi-number">
                                    <v-progress-circular v-if="loadingNT" color="primary"
                                                         indeterminate size="30" width="3" />
                                    <span v-else>{{ nTests }}</span>
                                </div>
                                <p class="text-body-2 text-medium-emphasis mt-1">Atividades Avaliativas</p>
                            </div>
                        </v-card-text>
                        <div class="painel-kpi-accent" style="background: #16324A;" />
                    </v-card>
                </v-col>

                <v-col cols="12" sm="4">
                    <v-card class="painel-kpi-card" elevation="0">
                        <v-card-text class="d-flex align-center ga-4 pa-5">
                            <div class="painel-kpi-icon" style="background: rgba(46,125,91,0.12);">
                                <v-icon color="success" icon="mdi-account-school" size="30" />
                            </div>
                            <div>
                                <div class="painel-kpi-number">
                                    <v-progress-circular v-if="loadingNS" color="success"
                                                         indeterminate size="30" width="3" />
                                    <span v-else>{{ nStudents }}</span>
                                </div>
                                <p class="text-body-2 text-medium-emphasis mt-1">Meus Alunos</p>
                            </div>
                        </v-card-text>
                        <div class="painel-kpi-accent" style="background: #2E7D5B;" />
                    </v-card>
                </v-col>
            </v-row>

            <!-- Gráfico de barras -->
            <v-row class="mt-2">
                <v-col>
                    <v-card elevation="0" class="painel-chart-card">
                        <v-card-text class="pb-2">
                            <div class="d-flex align-center justify-space-between flex-wrap ga-3">
                                <div>
                                    <p class="text-subtitle-1 font-weight-bold painel-title">
                                        Atividades Avaliativas por Mês
                                    </p>
                                    <p class="text-body-2 text-medium-emphasis">
                                        Total de atividades criadas no ano selecionado
                                    </p>
                                </div>
                                <v-select
                                    v-model="selectedYear"
                                    label="Ano"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :items="years"
                                    style="max-width: 120px;"
                                />
                            </div>
                        </v-card-text>
                        <v-card-text style="height: 300px;">
                            <Bar :data="chartData" :options="charOptions" />
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

        </template>

        <template v-else>
            <v-row class="fill-height" align="center" justify="center" style="min-height: 70vh;">
                <v-col cols="12" class="text-center mb-6">
                    <p class="text-h5 font-weight-bold painel-title">
                        Olá, {{ $store.state.userName }}
                    </p>
                </v-col>
                <v-col cols="12" md="5">
                    <v-card class="painel-brand-card pa-10 mx-auto" max-width="460" elevation="0">
                        <div class="d-flex align-center justify-center ga-3 mb-4">
                            <v-img
                                src="@/assets/img/PCSE.png"
                                height="56"
                                max-width="140"
                                contain
                            />
                            <v-icon size="48" color="white" icon="mdi-chart-timeline-variant-shimmer" />
                        </div>
                        <p class="text-h5 font-weight-bold text-white text-center mb-2">
                            TRACKLY
                        </p>
                        <p class="text-body-2 text-white text-center painel-brand-text mx-auto">
                            Trackly é a plataforma de gestão e acompanhamento do desempenho
                            acadêmico — reunindo notas, avaliações e indicadores dos alunos
                            em um só lugar para apoiar decisões pedagógicas.
                        </p>
                    </v-card>
                </v-col>
            </v-row>
        </template>

    </v-container>
</template>

<script src="@/assets/js/painel.js" />

<style scoped>
.painel-shell {
    padding-top: 8px;
}

.painel-title {
    color: #16324a;
    letter-spacing: 0.01em;
}

/* ── KPI cards ──────────────────────────────────────────────── */
.painel-kpi-card {
    border: 1px solid var(--pcse-border) !important;
    border-radius: 16px !important;
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.painel-kpi-card:hover {
    box-shadow: 0 8px 28px rgba(13, 31, 46, 0.10) !important;
    transform: translateY(-2px);
}

/* coloured left accent bar */
.painel-kpi-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 4px 0 0 4px;
}

.painel-kpi-icon {
    width: 58px;
    height: 58px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.painel-kpi-number {
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1;
    color: #16324a;
    min-height: 38px;
    display: flex;
    align-items: center;
}

/* ── Chart card ─────────────────────────────────────────────── */
.painel-chart-card {
    border: 1px solid var(--pcse-border) !important;
    border-radius: 16px !important;
}

/* ── Brand welcome card (non-professor roles) ──────────────── */
.painel-brand-card {
    background: var(--pcse-strong-panel);
    border-radius: 16px !important;
}

.painel-brand-text {
    opacity: 0.82;
    line-height: 1.65;
    max-width: 340px;
}
</style>
